import { useQuery } from '@apollo/client'
import { Col, Row, Skeleton } from 'antd'
import { useTranslation } from 'react-i18next'

import { LOT_CARBON_EMISSIONS, LotCarbonEmissions, LotCarbonEmissionsVariables } from 'api'
import { usePermissions } from 'hooks'
import { GraphQLUtils } from 'utils'

import { HistoricalIrrigation } from '../../../../api'
import { TargetYield } from '../../../TargetYield'
import { PendingEmergenceDateTooltip } from '../../../PendingEmergenceDateTooltip'
import { HistoricIrrigationCard, LotCarbonEmissionsCard } from './components'

type Values = {
  daysSinceEmergence: number
  chlorophyll: number
}
interface Props {
  hasEmergenceDate: boolean
  historicalIrrigationValues: HistoricalIrrigation['values']
  emergenceDate?: string
  yieldName?: string
  targetYieldCurveValues?: Values[]
  realYieldCurveValues?: Values[]
  lotId?: number
  isRiceLot: boolean
}

export const ChartCardsContainer: React.FC<Props> = ({
  historicalIrrigationValues,
  hasEmergenceDate,
  emergenceDate,
  yieldName,
  targetYieldCurveValues,
  realYieldCurveValues,
  lotId,
  isRiceLot,
}) => {
  const { t } = useTranslation('lot-dashboard')
  const { permissions } = usePermissions()
  const { data: carbonEmissionsData, loading: carbonEmissionsLoading } = useQuery<
    LotCarbonEmissions,
    LotCarbonEmissionsVariables
  >(LOT_CARBON_EMISSIONS, {
    variables: { lotId: lotId ?? 0 },
    skip: !permissions.carbonEmissions || !lotId || !isRiceLot,
    fetchPolicy: 'network-only',
    onError: GraphQLUtils.errorHandler,
  })

  return (
    <Row gutter={[0, 24]}>
      <PendingEmergenceDateTooltip hasEmergenceDate={hasEmergenceDate}>
        <Col span={24}>
          <HistoricIrrigationCard
            title={t('irrigationCard.title')}
            subTitle={`${t('irrigationCard.subtitle')} ${t('irrigationCard.hydricStatus')}`}
            historicalIrrigationValues={historicalIrrigationValues}
          />
        </Col>
      </PendingEmergenceDateTooltip>
      <Col span={24}>
        <TargetYield
          emergenceDate={emergenceDate}
          yieldName={yieldName}
          targetYieldCurveValues={targetYieldCurveValues}
          realYieldCurveValues={realYieldCurveValues}
          height="300px"
        />
      </Col>
      {permissions.carbonEmissions && isRiceLot && (
        <Col span={24}>
          <Skeleton active loading={carbonEmissionsLoading} paragraph={{ rows: 8 }} title={false}>
            <LotCarbonEmissionsCard emissions={carbonEmissionsData?.lot.carbonEmissions} />
          </Skeleton>
        </Col>
      )}
    </Row>
  )
}
